import React, { useEffect, useState } from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import type {
  BlastFrameCatalogueCarriageRow,
  BlastFrameCatalogueFrameSize,
  BlastFrameCatalogueKite,
  BlastFrameCatalogueVersion,
  InductionCoilCatalogueFrame,
  InductionCoilCatalogueVersion,
} from "../models/types";

export type BlastFrameCatalogueAddKind =
  | "frame-size"
  | "version"
  | "kite"
  | "carriage-row"
  | "ic-frame"
  | "ic-version";

export type BlastFrameCatalogueAddPayload =
  | { kind: "frame-size"; item: BlastFrameCatalogueFrameSize }
  | { kind: "version"; item: BlastFrameCatalogueVersion }
  | { kind: "kite"; item: BlastFrameCatalogueKite }
  | { kind: "carriage-row"; item: BlastFrameCatalogueCarriageRow }
  | { kind: "ic-frame"; item: InductionCoilCatalogueFrame }
  | { kind: "ic-version"; item: InductionCoilCatalogueVersion };

interface BlastFrameCatalogueAddModalProps {
  open: boolean;
  kind: BlastFrameCatalogueAddKind | null;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (payload: BlastFrameCatalogueAddPayload) => void;
}

const TITLES: Record<BlastFrameCatalogueAddKind, string> = {
  "frame-size": "Add Frame Size",
  version: "Add Length Version",
  kite: "Add Blast Carriage / Kite",
  "carriage-row": "Add Carriage Row",
  "ic-frame": "Add Induction Coil Frame",
  "ic-version": "Add Induction Coil Version",
};

const Field = ({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="modal-field">
    <label htmlFor={id}>{label}</label>
    {children}
    {error ? (
      <p className="op-field-error" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const parseRequiredNumber = (raw: string, label: string): { value?: number; error?: string } => {
  const trimmed = raw.trim();
  if (!trimmed) return { error: `${label} is required.` };
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { error: `${label} must be a number.` };
  return { value };
};

const parseOptionalBool = (raw: string) => raw === "true";

const BlastFrameCatalogueAddModal = ({
  open,
  kind,
  saving = false,
  error = null,
  onClose,
  onSave,
}: BlastFrameCatalogueAddModalProps) => {
  const [label, setLabel] = useState("");
  const [index, setIndex] = useState("");
  const [minMm, setMinMm] = useState("");
  const [maxMm, setMaxMm] = useState("");
  const [id, setId] = useState("");
  const [innerWheelLengthMm, setInnerWheelLengthMm] = useState("");
  const [overallLengthMm, setOverallLengthMm] = useState("");
  const [carriageLengthMm, setCarriageLengthMm] = useState("");
  const [heads, setHeads] = useState("Large Heads");
  const [requiresTwoTieBarBlast, setRequiresTwoTieBarBlast] = useState("true");
  const [requiresFourTieBarBlast, setRequiresFourTieBarBlast] = useState("false");
  const [steelOdInnerOffsetMm, setSteelOdInnerOffsetMm] = useState("");
  const [steelOdOuterOffsetMm, setSteelOdOuterOffsetMm] = useState("");
  const [clearanceOffsetsMm, setClearanceOffsetsMm] = useState("50");
  const [clearanceMarginMm, setClearanceMarginMm] = useState("10");
  const [usesSmallSeal, setUsesSmallSeal] = useState("false");
  const [frameSize, setFrameSize] = useState("");
  const [outerBlastCarriageHolePcdMm, setOuterBlastCarriageHolePcdMm] = useState("");
  const [nbIn, setNbIn] = useState("");
  const [clearanceType, setClearanceType] = useState("HSS");
  const [idMm, setIdMm] = useState("");
  const [bottomEndLengthMm, setBottomEndLengthMm] = useState("");
  const [insideWheelsLengthMm, setInsideWheelsLengthMm] = useState("");
  const [totalLengthSwivelPlateMm, setTotalLengthSwivelPlateMm] = useState("");
  const [heatLengthMm, setHeatLengthMm] = useState("");
  const [totalLengthTufnolDiscsMm, setTotalLengthTufnolDiscsMm] = useState("");
  const [insideTufnolDiscsLengthMm, setInsideTufnolDiscsLengthMm] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !kind) return;
    setLabel("");
    setIndex("");
    setMinMm("");
    setMaxMm("");
    setId("");
    setInnerWheelLengthMm("");
    setOverallLengthMm("");
    setCarriageLengthMm("");
    setHeads("Large Heads");
    setRequiresTwoTieBarBlast("true");
    setRequiresFourTieBarBlast("false");
    setSteelOdInnerOffsetMm("");
    setSteelOdOuterOffsetMm("");
    setClearanceOffsetsMm("50");
    setClearanceMarginMm("10");
    setUsesSmallSeal("false");
    setFrameSize("");
    setOuterBlastCarriageHolePcdMm("");
    setNbIn("");
    setClearanceType("HSS");
    setIdMm("");
    setBottomEndLengthMm("");
    setInsideWheelsLengthMm("");
    setTotalLengthSwivelPlateMm("");
    setHeatLengthMm("");
    setTotalLengthTufnolDiscsMm("");
    setInsideTufnolDiscsLengthMm("");
    setFieldErrors({});
  }, [open, kind]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, saving]);

  if (!open || !kind) return null;

  const submit = () => {
    const nextErrors: Record<string, string> = {};

    if (kind === "frame-size") {
      if (!label.trim()) nextErrors.label = "Label is required.";
      const indexParsed = parseRequiredNumber(index, "Index");
      if (indexParsed.error) nextErrors.index = indexParsed.error;
      const minParsed = parseRequiredNumber(minMm, "Min");
      if (minParsed.error) nextErrors.minMm = minParsed.error;
      const maxParsed = parseRequiredNumber(maxMm, "Max");
      if (maxParsed.error) nextErrors.maxMm = maxParsed.error;
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      onSave({
        kind,
        item: {
          label: label.trim(),
          index: indexParsed.value!,
          minMm: minParsed.value!,
          maxMm: maxParsed.value!,
        },
      });
      return;
    }

    if (kind === "version") {
      if (!id.trim()) nextErrors.id = "Version id is required.";
      const indexParsed = parseRequiredNumber(index, "Index");
      if (indexParsed.error) nextErrors.index = indexParsed.error;
      const innerParsed = parseRequiredNumber(innerWheelLengthMm, "Inner wheel length");
      if (innerParsed.error) nextErrors.innerWheelLengthMm = innerParsed.error;
      const overallParsed = parseRequiredNumber(overallLengthMm, "Overall length");
      if (overallParsed.error) nextErrors.overallLengthMm = overallParsed.error;
      const carriageParsed = parseRequiredNumber(carriageLengthMm, "Carriage length");
      if (carriageParsed.error) nextErrors.carriageLengthMm = carriageParsed.error;
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      onSave({
        kind,
        item: {
          id: id.trim(),
          index: indexParsed.value!,
          innerWheelLengthMm: innerParsed.value!,
          overallLengthMm: overallParsed.value!,
          carriageLengthMm: carriageParsed.value!,
        },
      });
      return;
    }

    if (kind === "kite") {
      if (!id.trim()) nextErrors.id = "Kite type is required.";
      if (!heads.trim()) nextErrors.heads = "Heads is required.";
      const innerParsed = parseRequiredNumber(steelOdInnerOffsetMm, "Steel OD inner offset");
      if (innerParsed.error) nextErrors.steelOdInnerOffsetMm = innerParsed.error;
      const outerParsed = parseRequiredNumber(steelOdOuterOffsetMm, "Steel OD outer offset");
      if (outerParsed.error) nextErrors.steelOdOuterOffsetMm = outerParsed.error;
      const marginParsed = parseRequiredNumber(clearanceMarginMm, "Clearance margin");
      if (marginParsed.error) nextErrors.clearanceMarginMm = marginParsed.error;
      const offsets = clearanceOffsetsMm
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => Number(part));
      if (offsets.length === 0 || offsets.some((value) => !Number.isFinite(value))) {
        nextErrors.clearanceOffsetsMm = "Enter one or more numbers, separated by commas.";
      }
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      onSave({
        kind,
        item: {
          id: id.trim(),
          heads: heads.trim(),
          requiresTwoTieBarBlast: parseOptionalBool(requiresTwoTieBarBlast),
          requiresFourTieBarBlast: parseOptionalBool(requiresFourTieBarBlast),
          steelOdInnerOffsetMm: innerParsed.value!,
          steelOdOuterOffsetMm: outerParsed.value!,
          clearanceOffsetsMm: offsets,
          clearanceMarginMm: marginParsed.value!,
          usesSmallSeal: parseOptionalBool(usesSmallSeal),
        },
      });
      return;
    }

    if (kind === "ic-frame") {
      const nbParsed = parseRequiredNumber(nbIn, "NB (in)");
      if (nbParsed.error) nextErrors.nbIn = nbParsed.error;
      const indexParsed = parseRequiredNumber(index, "Index");
      if (indexParsed.error) nextErrors.index = indexParsed.error;
      if (!clearanceType.trim()) nextErrors.clearanceType = "Clearance type is required.";
      const idParsed = parseRequiredNumber(idMm, "ID");
      if (idParsed.error) nextErrors.idMm = idParsed.error;
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      onSave({
        kind,
        item: {
          nbIn: nbParsed.value!,
          index: indexParsed.value!,
          clearanceType: clearanceType.trim(),
          idMm: idParsed.value!,
        },
      });
      return;
    }

    if (kind === "ic-version") {
      if (!id.trim()) nextErrors.id = "Version id is required.";
      const indexParsed = parseRequiredNumber(index, "Index");
      if (indexParsed.error) nextErrors.index = indexParsed.error;
      const bottomParsed = parseRequiredNumber(bottomEndLengthMm, "Bottom end length");
      if (bottomParsed.error) nextErrors.bottomEndLengthMm = bottomParsed.error;
      const insideWheelsParsed = parseRequiredNumber(insideWheelsLengthMm, "Inside wheels length");
      if (insideWheelsParsed.error) nextErrors.insideWheelsLengthMm = insideWheelsParsed.error;
      const swivelParsed = parseRequiredNumber(totalLengthSwivelPlateMm, "Total length (swivel plate)");
      if (swivelParsed.error) nextErrors.totalLengthSwivelPlateMm = swivelParsed.error;
      const heatParsed = parseRequiredNumber(heatLengthMm, "Heat length");
      if (heatParsed.error) nextErrors.heatLengthMm = heatParsed.error;
      const tufnolTotalParsed = parseRequiredNumber(totalLengthTufnolDiscsMm, "Total length (tufnol discs)");
      if (tufnolTotalParsed.error) nextErrors.totalLengthTufnolDiscsMm = tufnolTotalParsed.error;
      const tufnolInsideParsed = parseRequiredNumber(
        insideTufnolDiscsLengthMm,
        "Inside tufnol discs length",
      );
      if (tufnolInsideParsed.error) nextErrors.insideTufnolDiscsLengthMm = tufnolInsideParsed.error;
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      onSave({
        kind,
        item: {
          id: id.trim(),
          index: indexParsed.value!,
          bottomEndLengthMm: bottomParsed.value!,
          insideWheelsLengthMm: insideWheelsParsed.value!,
          totalLengthSwivelPlateMm: swivelParsed.value!,
          heatLengthMm: heatParsed.value!,
          totalLengthTufnolDiscsMm: tufnolTotalParsed.value!,
          insideTufnolDiscsLengthMm: tufnolInsideParsed.value!,
        },
      });
      return;
    }

    if (!frameSize.trim()) nextErrors.frameSize = "Frame size is required.";
    const pcdParsed = parseRequiredNumber(outerBlastCarriageHolePcdMm, "Outer blast carriage hole PCD");
    if (pcdParsed.error) nextErrors.outerBlastCarriageHolePcdMm = pcdParsed.error;
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSave({
      kind,
      item: {
        frameSize: frameSize.trim(),
        outerBlastCarriageHolePcdMm: pcdParsed.value!,
      },
    });
  };

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="dialog-card dialog-card--wide op-edit-config-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="op-cat-add-title"
      >
        <div className="dialog-header">
          <h2 className="dialog-title" id="op-cat-add-title">
            {TITLES[kind]}
          </h2>
          <CcButton variant="icon" aria-label="Close" onClick={onClose} disabled={saving}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body op-edit-config-modal__body">
          <div className="op-edit-grid">
            {kind === "frame-size" ? (
              <>
                <Field id="op-cat-label" label="Label" error={fieldErrors.label}>
                  <input
                    id="op-cat-label"
                    className="op-input"
                    placeholder='e.g. 54-60"'
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-index" label="Index" error={fieldErrors.index}>
                  <input
                    id="op-cat-index"
                    className="op-input"
                    inputMode="numeric"
                    value={index}
                    onChange={(event) => setIndex(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-min" label="Min (mm)" error={fieldErrors.minMm}>
                  <input
                    id="op-cat-min"
                    className="op-input"
                    inputMode="decimal"
                    value={minMm}
                    onChange={(event) => setMinMm(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-max" label="Max (mm)" error={fieldErrors.maxMm}>
                  <input
                    id="op-cat-max"
                    className="op-input"
                    inputMode="decimal"
                    value={maxMm}
                    onChange={(event) => setMaxMm(event.target.value)}
                  />
                </Field>
              </>
            ) : null}

            {kind === "version" ? (
              <>
                <Field id="op-cat-version-id" label="Version id" error={fieldErrors.id}>
                  <input
                    id="op-cat-version-id"
                    className="op-input"
                    placeholder="e.g. V21"
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-version-index" label="Index" error={fieldErrors.index}>
                  <input
                    id="op-cat-version-index"
                    className="op-input"
                    inputMode="numeric"
                    value={index}
                    onChange={(event) => setIndex(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-inner-wheel"
                  label="Inner wheel length (mm)"
                  error={fieldErrors.innerWheelLengthMm}
                >
                  <input
                    id="op-cat-inner-wheel"
                    className="op-input"
                    inputMode="decimal"
                    value={innerWheelLengthMm}
                    onChange={(event) => setInnerWheelLengthMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-overall"
                  label="Overall length (mm)"
                  error={fieldErrors.overallLengthMm}
                >
                  <input
                    id="op-cat-overall"
                    className="op-input"
                    inputMode="decimal"
                    value={overallLengthMm}
                    onChange={(event) => setOverallLengthMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-carriage"
                  label="Carriage length (mm)"
                  error={fieldErrors.carriageLengthMm}
                >
                  <input
                    id="op-cat-carriage"
                    className="op-input"
                    inputMode="decimal"
                    value={carriageLengthMm}
                    onChange={(event) => setCarriageLengthMm(event.target.value)}
                  />
                </Field>
              </>
            ) : null}

            {kind === "kite" ? (
              <>
                <Field id="op-cat-kite-id" label="Kite type" error={fieldErrors.id}>
                  <input
                    id="op-cat-kite-id"
                    className="op-input"
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-heads" label="Heads" error={fieldErrors.heads}>
                  <input
                    id="op-cat-heads"
                    className="op-input"
                    value={heads}
                    onChange={(event) => setHeads(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-two-tie" label="Requires 2-tie blast">
                  <select
                    id="op-cat-two-tie"
                    className="op-input"
                    value={requiresTwoTieBarBlast}
                    onChange={(event) => setRequiresTwoTieBarBlast(event.target.value)}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
                <Field id="op-cat-four-tie" label="Requires 4-tie blast">
                  <select
                    id="op-cat-four-tie"
                    className="op-input"
                    value={requiresFourTieBarBlast}
                    onChange={(event) => setRequiresFourTieBarBlast(event.target.value)}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
                <Field
                  id="op-cat-steel-inner"
                  label="Steel OD inner offset (mm)"
                  error={fieldErrors.steelOdInnerOffsetMm}
                >
                  <input
                    id="op-cat-steel-inner"
                    className="op-input"
                    inputMode="decimal"
                    value={steelOdInnerOffsetMm}
                    onChange={(event) => setSteelOdInnerOffsetMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-steel-outer"
                  label="Steel OD outer offset (mm)"
                  error={fieldErrors.steelOdOuterOffsetMm}
                >
                  <input
                    id="op-cat-steel-outer"
                    className="op-input"
                    inputMode="decimal"
                    value={steelOdOuterOffsetMm}
                    onChange={(event) => setSteelOdOuterOffsetMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-clearance-offsets"
                  label="Clearance offsets (mm, comma-separated)"
                  error={fieldErrors.clearanceOffsetsMm}
                >
                  <input
                    id="op-cat-clearance-offsets"
                    className="op-input"
                    value={clearanceOffsetsMm}
                    onChange={(event) => setClearanceOffsetsMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-clearance-margin"
                  label="Clearance margin (mm)"
                  error={fieldErrors.clearanceMarginMm}
                >
                  <input
                    id="op-cat-clearance-margin"
                    className="op-input"
                    inputMode="decimal"
                    value={clearanceMarginMm}
                    onChange={(event) => setClearanceMarginMm(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-small-seal" label="Uses small seal">
                  <select
                    id="op-cat-small-seal"
                    className="op-input"
                    value={usesSmallSeal}
                    onChange={(event) => setUsesSmallSeal(event.target.value)}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
              </>
            ) : null}

            {kind === "carriage-row" ? (
              <>
                <Field id="op-cat-frame-size" label="Frame size" error={fieldErrors.frameSize}>
                  <input
                    id="op-cat-frame-size"
                    className="op-input"
                    placeholder='e.g. 54-60"'
                    value={frameSize}
                    onChange={(event) => setFrameSize(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-pcd"
                  label="Outer blast carriage hole PCD (mm)"
                  error={fieldErrors.outerBlastCarriageHolePcdMm}
                >
                  <input
                    id="op-cat-pcd"
                    className="op-input"
                    inputMode="decimal"
                    value={outerBlastCarriageHolePcdMm}
                    onChange={(event) => setOuterBlastCarriageHolePcdMm(event.target.value)}
                  />
                </Field>
              </>
            ) : null}

            {kind === "ic-frame" ? (
              <>
                <Field id="op-cat-nb" label="NB (in)" error={fieldErrors.nbIn}>
                  <input
                    id="op-cat-nb"
                    className="op-input"
                    inputMode="decimal"
                    value={nbIn}
                    onChange={(event) => setNbIn(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-ic-index" label="Index" error={fieldErrors.index}>
                  <input
                    id="op-cat-ic-index"
                    className="op-input"
                    inputMode="numeric"
                    value={index}
                    onChange={(event) => setIndex(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-clearance-type" label="Clearance type" error={fieldErrors.clearanceType}>
                  <input
                    id="op-cat-clearance-type"
                    className="op-input"
                    placeholder="e.g. HSS"
                    value={clearanceType}
                    onChange={(event) => setClearanceType(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-id-mm" label="ID (mm)" error={fieldErrors.idMm}>
                  <input
                    id="op-cat-id-mm"
                    className="op-input"
                    inputMode="decimal"
                    value={idMm}
                    onChange={(event) => setIdMm(event.target.value)}
                  />
                </Field>
              </>
            ) : null}

            {kind === "ic-version" ? (
              <>
                <Field id="op-cat-ic-version-id" label="Version id" error={fieldErrors.id}>
                  <input
                    id="op-cat-ic-version-id"
                    className="op-input"
                    placeholder="e.g. V11"
                    value={id}
                    onChange={(event) => setId(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-ic-version-index" label="Index" error={fieldErrors.index}>
                  <input
                    id="op-cat-ic-version-index"
                    className="op-input"
                    inputMode="numeric"
                    value={index}
                    onChange={(event) => setIndex(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-bottom-end"
                  label="Bottom end length (mm)"
                  error={fieldErrors.bottomEndLengthMm}
                >
                  <input
                    id="op-cat-bottom-end"
                    className="op-input"
                    inputMode="decimal"
                    value={bottomEndLengthMm}
                    onChange={(event) => setBottomEndLengthMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-inside-wheels"
                  label="Inside wheels length (mm)"
                  error={fieldErrors.insideWheelsLengthMm}
                >
                  <input
                    id="op-cat-inside-wheels"
                    className="op-input"
                    inputMode="decimal"
                    value={insideWheelsLengthMm}
                    onChange={(event) => setInsideWheelsLengthMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-swivel"
                  label="Total length swivel plate (mm)"
                  error={fieldErrors.totalLengthSwivelPlateMm}
                >
                  <input
                    id="op-cat-swivel"
                    className="op-input"
                    inputMode="decimal"
                    value={totalLengthSwivelPlateMm}
                    onChange={(event) => setTotalLengthSwivelPlateMm(event.target.value)}
                  />
                </Field>
                <Field id="op-cat-heat" label="Heat length (mm)" error={fieldErrors.heatLengthMm}>
                  <input
                    id="op-cat-heat"
                    className="op-input"
                    inputMode="decimal"
                    value={heatLengthMm}
                    onChange={(event) => setHeatLengthMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-tufnol-total"
                  label="Total length tufnol discs (mm)"
                  error={fieldErrors.totalLengthTufnolDiscsMm}
                >
                  <input
                    id="op-cat-tufnol-total"
                    className="op-input"
                    inputMode="decimal"
                    value={totalLengthTufnolDiscsMm}
                    onChange={(event) => setTotalLengthTufnolDiscsMm(event.target.value)}
                  />
                </Field>
                <Field
                  id="op-cat-tufnol-inside"
                  label="Inside tufnol discs length (mm)"
                  error={fieldErrors.insideTufnolDiscsLengthMm}
                >
                  <input
                    id="op-cat-tufnol-inside"
                    className="op-input"
                    inputMode="decimal"
                    value={insideTufnolDiscsLengthMm}
                    onChange={(event) => setInsideTufnolDiscsLengthMm(event.target.value)}
                  />
                </Field>
              </>
            ) : null}
          </div>

          {error ? (
            <p className="op-field-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="dialog-footer">
          <CcButton variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </CcButton>
          <CcButton variant="primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Add"}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

export default BlastFrameCatalogueAddModal;
